import { OpenAIInfo } from "~/vrm_utils/VRMUtils";
import { api } from "~/api";
import { Buffer } from "buffer";
import { getCurrentCustomerId } from "~/AtomWithDynamoDB";
import { audioManager, tryPlayAudio } from "~/SoundUtils";

// 既存のemitterを使用
import { emitter } from "../voicevox/VoiceSynthesis";
import { removeDollarKeys } from "~/utils/chatMessageUtils";
import { showNotificationDialog } from "~/utils/notificationDialogUtils";
import { t } from "i18next";

// 音声追跡用のグローバル変数
let lastAudioId = 0;
let currentPlayingAudioId: number | null = null;

/**
 * OpenAI TTSを使用してテキストを音声合成し再生する
 *
 * @param inputText 音声合成するテキスト
 * @param speakerInfo OpenAI話者情報（Model/Voice）
 * @param volume 再生音量
 * @param setIsThinking 思考中フラグの更新関数
 * @param setIsProcessing 処理中フラグの更新関数
 * @param setIsTranscribing 文字起こし有効フラグの更新関数
 * @param setOutputText 出力テキストの更新関数
 * @param setIsInConversationCycle 会話サイクルフラグの更新関数
 * @param endExpression 再生終了時にemitterで通知する表情（デフォルト: "neutral"）。
 *                      "bow" を指定すると発話終了後にお辞儀モーションが再生される。
 */
const handleOpenAI = async (
  inputText: string,
  speakerInfo = OpenAIInfo,
  volume: number,
  setIsThinking?: (flag: boolean) => void,
  setIsProcessing?: (flag: boolean) => void,
  setIsTranscribing?: (flag: boolean) => void,
  setOutputText?: (text: string) => void,
  setIsInConversationCycle?: (flag: boolean) => void,
  endExpression: string = "neutral",
  playbackToken?: number
) => {
  const playWithSimpleLipSync = (audio: HTMLAudioElement) => {
    // 各音声に一意のIDを割り当て
    const audioId = ++lastAudioId;
    currentPlayingAudioId = audioId;

    let intervalId: number | null = null;

    audio.addEventListener("play", () => {
      // 音声再生中は定期的に口を動かす
      intervalId = window.setInterval(() => {
        const vowels = ["a", "e", "i", "o", "u"];
        const randomVowel = vowels[Math.floor(Math.random() * vowels.length)];
        emitter.emit("phoneme", randomVowel);
      }, 150);
    });

    audio.addEventListener("ended", () => {
      console.info("OpenAI TTS - Stopping mouth animation");
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
      emitter.emit("expression", endExpression); // 表情を通知
      emitter.emit("phoneme", "end"); // 再生終了を通知
      if (setIsThinking) setIsThinking(false);

      // audioManagerをクリア
      audioManager.clearAudio();

      // この音声が最新の音声か判定
      if (audioId === currentPlayingAudioId) {
        setTimeout(() => {
          // タイムアウト後も最新の音声なら文字起こしを有効化
          if (audioId === currentPlayingAudioId) {
            if (setIsTranscribing) setIsTranscribing(true);
            currentPlayingAudioId = null;
          }
        }, 1500);
      }
    });
    // if (setOutputText) setOutputText(inputText);
    // audioManagerに登録してから再生
    audioManager.setAudio(audio);
    tryPlayAudio(audio);
  };

  try {
    const customerId = await getCurrentCustomerId();

    // $$key$$ を取り除いたテキストを音声合成に使用
    const audioInputText = removeDollarKeys(inputText);

    const audioBuffer = await api.openai.TTS.query({
      customerId,
      inputText: audioInputText,
      model: speakerInfo.Model,
      voice: speakerInfo.Voice,
    });
    // Blobを作成してURLを生成
    const audioBlob = new Blob([Buffer.from(audioBuffer.data)], { type: "audio/mpeg" });
    const audioUrl = URL.createObjectURL(audioBlob);

    // 合成中に画面遷移などで別の再生がリクエストされた場合、この音声は古いので
    // 再生もチャット出力も行わずに破棄する
    if (playbackToken !== undefined && !audioManager.isLatestPlayback(playbackToken)) {
      console.info("OpenAI TTS - Skipping stale audio (playback superseded)");
      URL.revokeObjectURL(audioUrl);
      if (setIsThinking) setIsThinking(false);
      return;
    }

    const audio = new Audio(audioUrl);
    audio.volume = Math.max(0, Math.min(1, volume)); // 音量を0.0から1.0の範囲に制限

    // シンプルなリップシンクで再生
    playWithSimpleLipSync(audio);

    // チャット出力
    if (setOutputText) setOutputText(inputText);
  } catch (error) {
    console.error("Error occurred while synthesizing text:", error);
    if (setIsThinking) setIsThinking(false);
    if (setIsProcessing) setIsProcessing(false);
    if (setOutputText) setOutputText(t("audioPlaybackError"));
    if (setIsInConversationCycle) setIsInConversationCycle(false);
    showNotificationDialog(t("audioPlaybackErrorTitle"), "error");
  }
};

export default handleOpenAI;
